import {int, Nullable, ValuesList, ValuesListValue} from "../caos-def";
import VariantsList from "../widgets/variants_list";
import {
    SetVariantFunc,
    VARIANT_FLAG__C1,
    VARIANT_FLAG__C2,
    VARIANT_FLAG__C3,
    VARIANT_FLAG__CV,
    VARIANT_FLAG__DS,
    VARIANT_FLAG__SM
} from "../utils.variants";
import {CaosLib} from "../utils.caosdef";
import {camelToSnakeCase, escapeRegExp} from "../utils.string";
import {isNumeric} from "../utils";
import React from "react";


/**
 * Properties passed to ValuesList render function
 */
type ValuesListProps = {
    readonly caosLib: CaosLib;
    readonly list: ValuesList;
    readonly setVariant: SetVariantFunc;
    readonly show: Nullable<boolean>
    readonly useSeaMonkeys: boolean;
    readonly pushPopupItem: (kind: string, id: number) => void;
}


/**
 * Render function for a values list value
 * @param listId id of the parent value list
 * @param i
 * @param item the values list value to render
 * @param hasDescription whether this list contains any descriptions.
 *  This determines if description table fields are used
 * @param quote
 */
function value(
    listId: int,
    i: number,
    item: ValuesListValue,
    hasDescription: boolean,
    quote: string
) {
    let name = item.name;
    if (name.indexOf("::") > 0) {
        const components = name.split("::");
        name = "(" + components[components.length - 1] + ")";
    }
    return <tr
        key={`values-list-${listId}-item-${i}-${item.value}-${camelToSnakeCase(escapeRegExp(item.name))}`}
        className="values-list-value"
        data-has-description={item.description && item.description.trim().length > 0 ? "true" : "false"}>
        <td data-label='value'
            className="values-list-value-value"
        >
            {quote}{item.value.trim()}{quote}
        </td>
        <td data-label='name' className="values-list-value-name">{name}</td>
        {hasDescription
            ? <td className="values-list-value-description" data-label="description">
                {item.description && item.description.trim().length > 0
                    ? item.description
                    : ''
                }
            </td>
            : ''
        }
    </tr>
}


/**
 * Represents a full list of known values for a CAOS parameter
 * @param caosDef caos definitions
 * @param list the list to render
 * @param setVariant a react setter for variant. Used when clicking variant in variant bar
 * @constructor
 */
export default function ValuesListElement(
    {
        list,
        setVariant,
        useSeaMonkeys,
        show,
    }: ValuesListProps
) {

    const hasDescription = list
        .arguments
        .find(a => a.description && a.description.trim().length > 0) != null;

    // let variants: GameVariant[];

    const listName = list.name;
    const listId = list.id;

    // const variant = list.variant.toUpperCase() as ListVariant;

    const longText: boolean = list.arguments.findIndex(value => {
        return ((value.value ?? "").length + ((value.name ?? "").length) + ((value.description) ?? "").length) > 80;
    }) >= 0;

    const variants = list.variants

    const quote = list.arguments.findIndex(value => !isNumeric(value.value.trim())) >= 0
        ? "\""
        : ""

    show = show != false

    return <article
        id={`value-list-${list.id}`}
        data-item-type="values-list"
        data-id={list.id}
        data-name={listName}
        data-entry-type="values-list"
        data-name-first-letter={listName[0]}
        className={"values-list" + (longText ? " long-text" : "")}
        data-role="entry"
        data-show={show}
        aria-hidden={!show}
        data-has-description={hasDescription}
        data-variant-c1={!!(variants & VARIANT_FLAG__C1)}
        data-variant-c2={!!(variants & VARIANT_FLAG__C2)}
        data-variant-cv={!!(variants & VARIANT_FLAG__CV)}
        data-variant-c3={!!(variants & VARIANT_FLAG__C3)}
        data-variant-ds={!!(variants & VARIANT_FLAG__DS)}
        data-variant-sm={!!(variants & VARIANT_FLAG__SM)}
    >
        <header>
            <h3>{list.name}</h3>
            <VariantsList variants={variants} setVariant={setVariant} useSeaMonkeys={useSeaMonkeys}/>
        </header>
        {
            typeof list["description"] !== "undefined" && list.description
                ? <p className="description">{list.description}</p>
                : ""
        }
        <div className="scroll-panel">
            <table className="values-list-values" data-has-description={hasDescription ? 'true' : 'false'}>
                <thead>
                <tr>
                    <th className="values-list-value-value" data-label='value'>Value</th>
                    <th className="values-list-value-name" data-label='name'>Name</th>
                    {hasDescription
                        ? <th className="values-list-value-description" data-label='description'>Description</th>
                        : ''
                    }
                </tr>
                </thead>
                <tbody>
                {
                    list.arguments.map((item: ValuesListValue, i: number) => value(listId, i, item, hasDescription, quote))
                }
                </tbody>
            </table>
        </div>
    </article>
}